import { useContext, useEffect, useMemo, useState, useRef } from 'react';
import React from 'react';
import { ExperienceContext } from '../../contexts/ExperienceContext';
import CodeMirror, { EditorView } from '@uiw/react-codemirror';
import { useLocation, useNavigate } from 'react-router-dom';
import TableShimmer from '../UI/Shimmer/TableShimmer';
import { compareJson, getObjFromHtml, isValidJsonString, getFlattenExperienceJSON } from '../../utils/helper';
import { useUserContext } from '../../contexts/UserContext';
import { getTemplateById, saveTemplates } from '../../utils/APIs';
import { toast } from 'react-toastify';
import Loader from '../UI/Loader/Loader';
import { formatDate } from '../../utils/converter';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import TableView from './ChooseTemplate/Views/TableView';
import Dropdown from '../UI/Dropdown/Dropdown';
import GalleryView from './ChooseTemplate/Views/GalleryView';
import PreviewMode from './ChooseTemplate/Views/PreviewMode';
import PrimaryModal from '../UI/Modal/PrimaryModal';
import { permission } from '../../config/permission';
import { GridIcon, ImageIcon, ListIcon, Save, X, Search, Plus } from 'lucide-react';
import { Card, Button, Input, Toggle, SegmentedControl } from '../UI';

const ChooseTemplate = ({ title, selectedTemplate, setSelectedTemplate, offerJson, hideTemplates = false, type = "", isContentfulFlow = false, onChange, onNoTemplatesFound }) => {
    const { rawData, getTemplatesRawData, loading } = useContext(ExperienceContext);
    const { enums, content } = useContentfulContext();

    const [rowData, setRowData] = useState([]);
    const [allRowData, setAllRowData] = useState([]);
    const [gridApi, setGridApi] = useState(null);
    const [activeFilters, setActiveFilters] = useState(['local', 'global']);
    const navigator = useNavigate();
    const { pathname } = useLocation();
    const { userData, checkPermission } = useUserContext();
    const [loadingForData, setLoadingForData] = useState(false);
    const [viewMode, setViewMode] = useState('list');

    const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState("");
    const [isCreating, setIsCreating] = useState(false);
    const [createError, setCreateError] = useState("");

    const isListViewEnable = checkPermission(permission.CREATE_EXP_OP_CHOOSE_TEMPLATE_VIEW_LIST)
    const isGalleryViewEnable = checkPermission(permission.CREATE_EXP_OP_CHOOSE_TEMPLATE_VIEW_GALLERY)
    const isPreviewViewEnable = checkPermission(permission.CREATE_EXP_OP_CHOOSE_TEMPLATE_VIEW_PREVIEW)

    const initialCode = useMemo(() => {
        if (!offerJson?.content) return "";
        if (typeof offerJson.content === 'string') return offerJson.content;
        return JSON.stringify(offerJson.content, null, 2);
    }, [offerJson?.content]);

    const [localCode, setLocalCode] = useState(initialCode);

    useEffect(() => {
        setLocalCode(initialCode);
    }, [initialCode]);

    useEffect(() => {
        getTemplatesRawData(true);
    }, []);

    useEffect(() => {
        if (rawData?.length) {
            const pathParts = pathname.split('/');
            const lastPart = pathParts[pathParts.length - 1];
            let jsonOrContent = rawData;

            if (type !== "") {
                if (type === "json") {
                    jsonOrContent = rawData.filter((x) => x.type !== enums?.templateTypes.json_a_to_html.id);
                }
            } else {
                if (lastPart.includes('json')) {
                    jsonOrContent = rawData.filter((x) => x.type !== enums?.templateTypes.json_a_to_html.id);
                } else if (lastPart.includes('content')) {
                    jsonOrContent = rawData.filter((x) => x.type === enums?.templateTypes.json_a_to_html.id);
                }
            }

            if (offerJson?.content || offerJson?.compareContent) {
                const contentToCompare = offerJson?.compareContent;
                jsonOrContent = jsonOrContent.filter((x) => {
                    let isMatched;

                    if (x.type === enums?.templateTypes.json_a_to_html.id) {
                        // For html-type templates, always compare against actual content
                        const parsedJsonSchema = JSON.parse(x?.json_schema)
                        const offerJsonObj = getObjFromHtml(offerJson?.content, parsedJsonSchema?.config_obj_key);
                        if (!offerJsonObj) return false;

                        const temp = JSON.parse(x?.json_schema);
                        delete temp?.config_obj_key

                        isMatched = compareJson(temp, offerJsonObj);
                        return isMatched.length == 0;
                    } else {
                        const invalidJson = isValidJsonString(x.json_schema)
                        if (!invalidJson) return false;

                        if (Array.isArray(contentToCompare) && contentToCompare.length > 0) {
                            // contentToCompare is an array of content type field IDs.
                            // Exact match: template keys and field IDs must be the same set (same count + all keys present)
                            const templateTopLevelKeys = Object.keys(JSON.parse(x.json_schema));
                            if (templateTopLevelKeys.length === 0) return false;
                            return (
                                templateTopLevelKeys.length === contentToCompare.length &&
                                templateTopLevelKeys.every(key => contentToCompare.includes(key))
                            );
                        } else if (contentToCompare && !Array.isArray(contentToCompare)) {
                            isMatched = compareJson(JSON.parse(x.json_schema), contentToCompare);
                            return isMatched.length == 0;
                        } else {
                            // compareContent is null/undefined — fall back to comparing against actual content.
                            // This handles the single-JSON-field case where the inner JSON is in offerJson.content.
                            isMatched = compareJson(JSON.parse(x.json_schema), offerJson?.content);
                            return isMatched.length == 0 || (isMatched.length == 1 && isMatched.includes("experience_id"));
                        }
                    }
                })
            }

            const filteredData = jsonOrContent.filter((item) => activeFilters.includes(item.scope));

            setRowData(() => {
                const formattedData = filteredData.map((item) => {
                    const hasAllRequiredFields = 'html' in item && 'css' in item && 'json_schema' in item;
                    return {
                        ...item,
                        template_name: item.name,
                        icon_url: item.icon_url,
                        id: item._id,
                        type: item.type,
                        scope: item.scope,
                        last_edited: formatDate(item.updatedAt),
                        managed_by: item.managed_by,
                        isDisabled: !hasAllRequiredFields
                    }
                });

                formattedData.sort((a, b) => new Date(b.last_edited) - new Date(a.last_edited));
                setAllRowData(formattedData);
                return formattedData;
            })
        }
    }, [rawData, activeFilters]);

    const toastShownRef = useRef(false);

    useEffect(() => {
        if (!loading && rawData?.length > 0) {
            if (rowData.length === 0) {
                if (!toastShownRef.current && !hideTemplates && isContentfulFlow) {
                    toast.warn("There is no template found for this JSON schema");
                    toastShownRef.current = true;
                }
                if (onNoTemplatesFound) onNoTemplatesFound(true);
            } else {
                if (onNoTemplatesFound) onNoTemplatesFound(false);
            }
        }
    }, [loading, rawData?.length, rowData.length, hideTemplates, isContentfulFlow, onNoTemplatesFound]);

    const onFilterTextBoxChanged = (event) => {
        const filterValue = event.target.value.toLowerCase();
        if (viewMode === 'list') {
            gridApi.setFilterModel({
                template_name: { type: 'contains', filter: filterValue }
            });
        }

        if (viewMode === 'gallery') {
            if (!filterValue) {
                setRowData(allRowData);
                return;
            }
            const filteredData = allRowData.filter((item) => {
                return item.template_name.toLowerCase().includes(filterValue);
            });
            setRowData(filteredData);
        }
    };

    const handleFilterChange = (filter) => {
        setActiveFilters((prevFilters) =>
            prevFilters.includes(filter)
                ? prevFilters.filter((f) => f !== filter)
                : [...prevFilters, filter]
        );
    };

    const handleCreateTemplate = async (e) => {
        e.preventDefault();
        if (!newTemplateName.trim()) {
            setCreateError("Template name is required.");
            return;
        }

        try {
            setIsCreating(true);
            setCreateError("");

            const sourceObj = offerJson.content || {};
            const configJsonArray = [];
            let index = 1;

            Object.keys(sourceObj).forEach((key) => {
                const value = sourceObj[key];
                if (typeof value === 'object' && value !== null) {
                    return;
                }

                configJsonArray.push({
                    order: index++,
                    exp_json_field: key,
                    id: key,
                    input: {
                        input_label: key,
                        input_type: "text",
                        input_default_value: value !== null ? String(value) : "",
                        input_helptext: ""
                    },
                    rules: {
                        is_mandatory: false,
                        min_length: "",
                        max_length: ""
                    }
                });
            });

            const payload = {
                name: newTemplateName,
                html: "",
                css: "",
                type: enums?.templateTypes?.no_preview?.id || "no_preview",
                config_json: JSON.stringify(configJsonArray),
                json_schema: JSON.stringify(offerJson.content)
            };

            const result = await saveTemplates(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                payload,
                undefined
            );

            toast.success("Template created successfully");

            await getTemplatesRawData(true);
            setShowCreateTemplateModal(false);
            setNewTemplateName("");
        } catch (error) {
            setCreateError(error?.message || "Failed to create template. Please try again.");
            toast.error(error?.message || "Failed to create template.");
        } finally {
            setIsCreating(false);
        }
    };

    const handleSelectionChanged = async (data) => {
        setLoadingForData(true);
        if (data?.source === "api" && !userData?.defaultAEMAdobeIO) {
            toast.error(content.CHOOSE_TEMPLATE_CONTENT.error.no_aem_found);
            setLoadingForData(false);
            return;
        }

        try {
            const result = await getTemplateById(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                data.id,
                userData?.defaultAEMAdobeIO?._id
            );

            setSelectedTemplate(result.data);
        } catch (error) {
            toast.error(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
            navigator("/dashboard/experience/manage-experiences");
        } finally {
            setLoadingForData(false);
            return
        };
    };

    const renderView = () => {
        switch (viewMode) {
            case 'gallery':
                return isGalleryViewEnable && <GalleryView rowData={rowData} handleOnClickCallback={handleSelectionChanged} />;
            case 'preview':
                return isPreviewViewEnable && (
                    <PreviewMode
                        rowData={rowData}
                        handleCancelClick={() => setViewMode('list')}
                        handleOnClickCallback={handleSelectionChanged}
                    />
                )
            case 'list':
            default:
                return (
                    <TableView
                        rowData={rowData}
                        setGridApi={setGridApi}
                        handleOnClickCallback={handleSelectionChanged}
                    />
                );
        }
    };


    return (
        <div className='flex gap-6 flex-col'>
            {!hideTemplates && <>
                <div className="flex flex-col space-y-1">
                    <span className="stride-eyebrow">Step 1</span>
                    <h1 className="text-xl md:text-2xl font-bold tracking-tight text-stride-heading">{content.CHOOSE_TEMPLATE_CONTENT.title}</h1>
                    <p className="text-xs md:text-sm text-stride-muted">{content.CHOOSE_TEMPLATE_CONTENT.description}</p>
                </div>
                {!loading && rawData?.length == 0 && !isContentfulFlow && (
                    <Card className="text-center p-12 max-w-lg mx-auto my-8">
                        <p className="text-base text-stride-heading font-medium mb-4">
                            {content.CHOOSE_TEMPLATE_CONTENT.no_template_found}
                        </p>
                        <Button
                            variant="primary"
                            icon={<Plus className="w-4 h-4" />}
                            onClick={() => navigator('/experience/create-template')}
                        >
                            {content.CHOOSE_TEMPLATE_CONTENT.create_new_template}
                        </Button>
                    </Card>
                )}
                {!loading && rowData.length === 0 && isContentfulFlow && (
                    <Card className="text-center p-8 max-w-lg mx-auto my-6">
                        <p className="text-base text-stride-heading font-medium mb-4">
                            No matching templates found.
                        </p>
                        <Button
                            variant="primary"
                            icon={<Plus className="w-4 h-4" />}
                            onClick={() => setShowCreateTemplateModal(true)}
                        >
                            Create Template
                        </Button>
                    </Card>
                )}
                {loadingForData && <Loader />}

                {(loading || !isContentfulFlow || allRowData?.length > 0) && (
                    <>
                        {!loading && (
                            <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-4'>
                                <div className='flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto'>
                                    <div className="w-full sm:w-72">
                                        <Input
                                            id='test-id-for-template-search'
                                            placeholder={content.CHOOSE_TEMPLATE_CONTENT.search_placeholder}
                                            onChange={onFilterTextBoxChanged}
                                            prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                            clearable
                                        />
                                    </div>
                                    <div className="flex items-center gap-4 px-3 py-1.5 bg-white rounded-lg border border-border shadow-xs">
                                        {['local', 'global'].map((type) => {
                                            const isActive = activeFilters.includes(type);
                                            const label = content?.CHOOSE_TEMPLATE_CONTENT?.[type] || (type === 'local' ? 'Local' : 'Global');
                                            return (
                                                <Toggle
                                                    key={type}
                                                    id={`choose-template-scope-${type}`}
                                                    checked={isActive}
                                                    onChange={() => handleFilterChange(type)}
                                                    label={label}
                                                    size="sm"
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                                <SegmentedControl
                                    value={viewMode}
                                    onChange={setViewMode}
                                    size="sm"
                                    variant="subtle"
                                    options={[
                                        isListViewEnable && {
                                            value: 'list',
                                            label: content.CHOOSE_TEMPLATE_CONTENT.view_mode.list,
                                            icon: <ListIcon size={14} />,
                                        },
                                        isGalleryViewEnable && {
                                            value: 'gallery',
                                            label: content.CHOOSE_TEMPLATE_CONTENT.view_mode.gallery,
                                            icon: <GridIcon size={14} />,
                                        },
                                        isPreviewViewEnable && {
                                            value: 'preview',
                                            label: content.CHOOSE_TEMPLATE_CONTENT.view_mode.preview,
                                            icon: <ImageIcon size={14} />,
                                        },
                                    ].filter(Boolean)}
                                />
                            </div>
                        )}
                        {loading && <TableShimmer colSize={4} rowSize={3} />}
                        {!loading && rawData.length > 0 && (
                            renderView()
                        )}
                    </>
                )}
            </>}
            {
                offerJson?.content && <div>
                    <div className="flex justify-between items-center py-3">
                        <div className="flex gap-4 items-center">
                            <h1 className="text-xl font-medium leading-tight tracking-tight text-gray-900 md:text-2xl">{title}</h1>
                        </div>
                    </div>
                    <CodeMirror
                        value={localCode}
                        height="400px"
                        readOnly={true}
                        editable={false}
                        lineWrapping={true}
                        extensions={[EditorView.lineWrapping]}
                    />
                </div>
            }
            {showCreateTemplateModal && (
                <PrimaryModal title="Create Template" hideModal={() => setShowCreateTemplateModal(false)}>
                    <form onSubmit={handleCreateTemplate} className="p-6 space-y-6">
                        <Input
                            id="new_template_name"
                            label="Template Name"
                            value={newTemplateName}
                            onChange={(e) => setNewTemplateName(e.target.value)}
                            placeholder="Enter template name"
                            mandatory
                            required
                            error={createError}
                        />
                        {createError && (
                            <p className="text-red-500 text-sm">{createError}</p>
                        )}
                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                            <Button variant="secondary" onClick={() => setShowCreateTemplateModal(false)} type="button">
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" loading={isCreating} disabled={isCreating}>
                                {isCreating ? "Creating..." : "Create"}
                            </Button>
                        </div>
                    </form>
                </PrimaryModal>
            )}
        </div >
    )
}

export default ChooseTemplate;